import { useContext, useEffect, useRef } from 'react';
import { audioPath } from 'constants/audioConstants';
import GlobalContext from 'contexts/GlobalContext';
import { useAppSelector } from 'hooks';
import useSound from 'hooks/useSound';
import {
  selectActiveOffer,
  selectMySpotFor,
  selectMySpotHasValidOffer
} from 'reducers/waitlist.selectors';

import useWaitlistCountdown from './useWaitlistCountdown';

// Final window (seconds) over which a soft countdown re-ping fires once per
// second: 5, 4, 3, 2, 1.
const REPING_WINDOW_SECONDS = 5;
// Each successive re-ping is pitched slightly higher to build a gentle rising
// urgency instead of a flat repeated beep.
const REPING_PITCH_STEP = 0.06;
// The last opportunity we chimed for, persisted so a page refresh mid-countdown
// does not re-alert for the SAME 15s window (AC8).
const SESSION_KEY = 'hijack_waitlist_alerted_key';

const readStoredKey = (): string | null => {
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
};

const persistKey = (key: string): void => {
  try {
    sessionStorage.setItem(SESSION_KEY, key);
  } catch {
    /* storage unavailable (private mode / SSR) — ref-based dedup still holds */
  }
};

/**
 * Audible "Table Ready" waitlist alert. Mounted ONCE at the app level, it is the
 * single source of the alert so it can never stack or double-fire (AC8/AC9), and
 * it plays regardless of which table currently has focus (AC7).
 *
 * Driven entirely by redux `state.waitlist.pendingOffer` — the reducer already
 * serialises to a single active offer at a time — and by the SAME
 * `useWaitlistCountdown` the visual JOIN chip / lobby card use, so the audio and
 * the on-screen countdown are inherently in sync (AC1/AC2).
 *
 * Direction: a warm, inviting rising chime (a "come on over", not an alarm).
 *   • Initial:  a rising mallet-chime the moment the window opens.
 *   • Final 5s: a soft re-ping once per second (5,4,3,2,1), each pitched a touch
 *               higher for a gentle rising urgency — no fatiguing looping bed (AC3).
 * Both cues are short one-shots that fade out on their own, so on resolve
 * (join / decline / expiry) they simply ring out — no hard cut (AC4/AC5).
 *
 * Respects the GLOBAL mute (`isMuted`, the Howler master mute) but deliberately
 * ignores the per-table "Mute Other Players" setting — a seat becoming ready is
 * a critical self-notification, not a non-critical table/opponent sound (AC6).
 *
 * Unlike every other game SFX (which are gated to table surfaces by the useSound
 * wrapper), this alert passes `alwaysPlay` so it is ALSO heard in the lobby —
 * the whole point is to reach a waiting player wherever they are (AC7).
 */
const useWaitlistAlertSound = (): void => {
  const { isMuted } = useContext(GlobalContext);

  const offer = useAppSelector(selectActiveOffer);
  const gameId = offer?.gameId ?? 0;
  const mySpot = useAppSelector(selectMySpotFor(gameId));
  const hasValidOffer = useAppSelector(selectMySpotHasValidOffer(gameId));
  const acceptedOfferGameId = useAppSelector((state) => state.waitlist.acceptedOfferGameId);

  const { secondsRemaining, expired, active } = useWaitlistCountdown({
    waitForAcceptStartTime: mySpot?.waitForAcceptStartTime,
    waitlistTimeOut: mySpot?.waitlistTimeOut ?? 0,
    bumpCount: mySpot?.bumpCount
  });

  // alwaysPlay: this alert must be heard in the LOBBY too, so it opts out of the
  // table-view sound gate that silences all other game SFX outside a table.
  const [playChime, { stop: stopChime }] = useSound(audioPath.waitlistSeatReady, {
    volume: 0.75,
    alwaysPlay: true
  });
  const [playReping, { stop: stopReping }] = useSound(audioPath.waitlistReping, {
    volume: 0.4,
    alwaysPlay: true
  });

  // The player has already claimed THIS offer — treat it as resolved so we don't
  // keep re-alerting, even though the engine re-broadcasts the offer until they
  // are actually seated.
  const accepted = acceptedOfferGameId !== null && acceptedOfferGameId === gameId;

  // A genuinely live, alertable opportunity right now.
  const live = Boolean(offer) && gameId > 0 && hasValidOffer && active && !expired && !accepted;

  // Identity of the current opportunity. A new gameId, a re-offer (new start
  // time) or a server bump all mint a new key → a fresh alert; a re-render,
  // reconnect or refresh keeps the same key → no re-alert (AC8).
  const opportunityKey = live
    ? `${gameId}:${mySpot?.waitForAcceptStartTime ?? ''}:${mySpot?.bumpCount ?? 0}`
    : null;

  // Seed the "already alerted" latch from sessionStorage on the very first
  // render (before any effect runs) so a refresh mid-window is deduped.
  const alertedKeyRef = useRef<string | null>(null);
  const seededRef = useRef(false);
  if (!seededRef.current) {
    seededRef.current = true;
    alertedKeyRef.current = readStoredKey();
  }
  // Tracks the last "opportunity:second" we re-pinged, so each of the final
  // seconds fires exactly once even across re-renders / reconnects (AC8).
  const lastRepingTagRef = useRef<string | null>(null);

  // Fire the cues.
  useEffect(() => {
    if (!live || !opportunityKey || isMuted) return;

    // 1) Initial rising chime — exactly once per opportunity.
    if (alertedKeyRef.current !== opportunityKey) {
      alertedKeyRef.current = opportunityKey;
      persistKey(opportunityKey);
      lastRepingTagRef.current = null; // arm the re-pings for this new opportunity
      playChime();
    }

    // 2) Countdown re-ping once per second through the final stretch (5..1),
    //    each a touch higher-pitched to build a gentle rising urgency.
    if (secondsRemaining <= REPING_WINDOW_SECONDS && secondsRemaining > 0) {
      const tag = `${opportunityKey}:${secondsRemaining}`;
      if (lastRepingTagRef.current !== tag) {
        lastRepingTagRef.current = tag;
        const step = REPING_WINDOW_SECONDS - secondsRemaining; // 0 at 5s → 4 at 1s
        playReping({ playbackRate: 1 + step * REPING_PITCH_STEP });
      }
    }
  }, [live, opportunityKey, secondsRemaining, isMuted, playChime, playReping]);

  // A global mute silences the alert immediately. On a normal resolve the cues
  // are short one-shots and are left to ring out (no hard cut).
  useEffect(() => {
    if (isMuted) {
      stopChime();
      stopReping();
    }
  }, [isMuted, stopChime, stopReping]);
};

export default useWaitlistAlertSound;
